<template>
  <div class="community-actors">
    <div class="tw-container px-4 py-6">

      <!-- Header -->
      <div class="mb-6 tw-flex flex-wrap items-start justify-between gap-3">
        <div>
          <h2 class="text-2xl font-bold text-gray-900">{{ actorsLabel }}</h2>
          <p class="text-sm text-gray-500 mt-1">
            {{ total !== null ? $t('community_actors_count', { count: total }) : $t('community_actors_loading') }}
          </p>
        </div>
        <DsButton
          v-if="canAddActors"
          variant="rounded"
          icon="plus"
          :label="$t('community_add_actor_button', { actor: actorLabel })"
          @click="displayAddActorModal"
        />
      </div>

      <!-- Search -->
      <div class="mb-6 bg-white rounded-lg shadow-sm border p-4">
        <DsInput
          v-model="filters.q"
          icon="search"
          is-simplified
          :placeholder="$t('community_actors_search_placeholder')"
        />
      </div>

      <!-- Loading -->
      <div v-if="loading" class="tw-flex items-center justify-center py-16">
        <Loading/>
      </div>

      <!-- Empty state -->
      <div v-else-if="actors.length === 0" class="text-center py-16 px-8 bg-white rounded-lg border border-gray-200">
        <div class="bg-gray-100 rounded-full tw-flex items-center justify-center mx-auto mb-4 text-gray-400" style="width:56px;height:56px;flex-shrink:0">
          <Icon name="building" size="24"/>
        </div>
        <p class="text-base font-semibold text-gray-700 mb-1">{{ $t('community_actors_empty_title') }}</p>
        <p class="text-sm text-gray-400">{{ filters.q ? $t('community_actors_empty_search_sub') : $t('community_actors_empty_sub') }}</p>
      </div>

      <!-- Actor list -->
      <div v-else class="tw-flex flex-col gap-3">
        <div
          v-for="actor in actors"
          :key="actor.id"
          class="community-actors__row tw-flex flex-wrap items-center gap-4 bg-white rounded-lg border border-gray-200 px-5 py-4 shadow-sm transition-shadow hover:shadow-md"
          :class="{ 'community-actors__row--removing': isRemoving(actor) }"
        >
          <!-- Logo -->
          <div
            class="community-actors__logo shrink-0"
            :style="logoStyle(actor)"
          >
            <span v-if="!logoUrl(actor)">{{ initials(actor.name) }}</span>
          </div>

          <!-- Info -->
          <div class="tw-flex flex-col gap-0.5 flex-1 min-w-0">
            <router-link
              :to="`/communities/${communityId}/actors/${actor.elastic_id}`"
              class="font-semibold text-gray-900 no-underline hover:underline truncate"
            >
              {{ actor.name }}
            </router-link>
            <div v-if="location(actor)" class="text-sm text-gray-500 truncate">{{ location(actor) }}</div>
            <a
              v-if="actor.url"
              :href="actor.url"
              target="_blank"
              rel="noopener noreferrer"
              class="text-xs text-gray-400 hover:text-primary no-underline truncate"
            >
              {{ actor.url }}
            </a>
          </div>

          <!-- Actions -->
          <div class="w-full sm:w-auto tw-flex items-center gap-2 flex-wrap justify-end">
            <!-- Contributors: shows which actors are actually represented by a user -->
            <div v-if="actor.contributors && actor.contributors.length" class="community-actors__contributors tw-flex items-center">
              <span
                v-for="contributor in actor.contributors.slice(0, 3)"
                :key="contributor.id"
                class="community-actors__contributor"
                :class="{ 'community-actors__contributor--clickable': canStartConversation(contributor) }"
                v-tooltip.top="contributorTooltip(contributor)"
                :role="canStartConversation(contributor) ? 'button' : null"
                :tabindex="canStartConversation(contributor) ? 0 : null"
                @click="chatWithContributor(contributor)"
                @keydown.enter="chatWithContributor(contributor)"
                @keydown.space.prevent="chatWithContributor(contributor)"
              >
                <Avatar :src="contributor.photo_url" :alt="contributor.name" width="26px" height="26px"/>
              </span>
              <span
                v-if="actor.contributors.length > 3"
                class="community-actors__contributor-overflow"
                v-tooltip.top="overflowTooltip(actor)"
              >+{{ actor.contributors.length - 3 }}</span>
            </div>

            <DsButton
              size="small"
              variant="rounded-secondary"
              :class="{ 'community-actors__remove--busy': isRemoving(actor) }"
              :icon="isRemoving(actor) ? 'spinner' : 'trash'"
              :disabled="isRemoving(actor)"
              :label="isRemoving(actor) ? $t('community_actors_removing') : $t('community_actors_remove')"
              @click="confirmRemove(actor)"
            />
          </div>
        </div>
      </div>

      <!-- Pagination -->
      <Pagination
        v-if="paging.next || paging.previous"
        class="mt-4"
        :paging="paging"
        @change="setOffset"
      />
    </div>
  </div>
</template>

<script lang="ts">
  import { defineComponent } from 'vue'
  import _throttle from 'lodash/throttle'
  import DsInput from '@/components/Form/DsInput.vue'
  import DsButton from '@/components/DsButton/DsButton.vue'
  import Icon from '@/components/Icon/Icon.vue'
  import Avatar from '@/components/Avatar/Avatar.vue'
  import Loading from '@/components/Dashboard/ConceptMap/Loading.vue'
  import Pagination from '@/components/Pagination/SimplePagination.vue'

  import { fetchCommunityActors, removeActorFromCommunity } from '@/api/communities'
  import { actorLogo } from '@/util/actor'
  import { trackMatomoEvent } from '@/util/analytics'
  import { MATOMO_EVENT_ACTIONS, MATOMO_EVENT_CATEGORIES } from '@/constants/analytics-constants.js'
  import { MUTATION_TYPES as UI_MUTATION_TYPES } from '@/store/modules/ui.js'
  import MODAL_IDS from '@/constants/modal-ids.js'
  import TranslationsMixin from '@/util/TranslationsMixin.js'
  import CommunityMixin from '@/util/CommunityMixin'
  import { ConversationsMixin } from '@/util/ConversationsMixin.js'

  export default defineComponent({
    name: 'CommunityActors',
    components: { DsInput, DsButton, Icon, Avatar, Loading, Pagination },
    mixins: [TranslationsMixin, CommunityMixin, ConversationsMixin],
    props: {
      communityId: {
        type: [Number, String],
        required: true,
      },
    },
    data () {
      return {
        actors: [] as any[],
        removingIds: [] as number[],
        loading: false,
        total: null as number | null,
        filters: {
          limit: 25,
          offset: 0,
          q: null as string | null,
        },
      }
    },
    computed: {
      // The detail store is the one that is guaranteed to be loaded on this route, unlike the
      // communities list the mixin's getCommunity() reads from
      community (): Record<string, any> | null {
        return this.$store.state.communities.detail.data || this.getCommunity() || null
      },
      // Actors are added to the community's portfolio, so without one there is nothing to add to
      canAddActors (): boolean {
        return !!this.community?.portfolioId
      },
      paging (): Record<string, any> {
        const { limit, offset } = this.filters
        const total = this.total ?? 0
        const paging: Record<string, any> = { total, limit }

        if (offset > 0) {
          paging.previous = { offset: Math.max(0, offset - limit) }
        }

        if (offset + limit < total) {
          paging.next = { offset: offset + limit }
          paging.last = { offset: Math.floor((total - 1) / limit) * limit }
        }

        return paging
      },
    },
    methods: {
      displayAddActorModal () {
        this.$store.commit(UI_MUTATION_TYPES.SET_MODAL_CONTEXT, {
          communityId: this.communityId,
          communityName: this.community?.name,
          portfolioId: this.community?.portfolioId,
        })
        this.$store.commit(UI_MUTATION_TYPES.SHOW_MODAL, MODAL_IDS.ADD_ACTOR_TO_COMMUNITY_MODAL)
      },
      initials (name: string): string {
        if (!name) return '?'
        return name.split(' ').slice(0, 2).map((part: string) => part[0]).join('').toUpperCase()
      },
      logoUrl (actor: any): string | null {
        return actorLogo(actor) || null
      },
      logoStyle (actor: any): Record<string, string> {
        const logo = this.logoUrl(actor)

        return logo ? { backgroundImage: `url(${logo})` } : {}
      },
      location (actor: any): string {
        return [actor.city, actor.country].filter(Boolean).join(', ')
      },
      contributorTooltip (contributor: any): string {
        const role = contributor.claim_type === 'actor-owner'
          ? this.$t('community_actors_contributor_owner')
          : this.$t('community_actors_contributor_contributor')

        const tooltip = `${contributor.name} — ${role}`

        if (!this.canStartConversation(contributor)) {
          return tooltip
        }

        return `${tooltip} · ${this.$t('community_actors_contributor_chat')}`
      },
      chatWithContributor (contributor: any) {
        if (!this.canStartConversation(contributor)) {
          return
        }

        // Provided by ConversationsMixin: reuses an existing conversation when there is one
        this.startConversation(contributor.id)
      },
      overflowTooltip (actor: any): string {
        return actor.contributors
          .slice(3)
          .map((contributor: any) => contributor.name)
          .join(', ')
      },
      confirmRemove (actor: any) {
        this.$store.commit(UI_MUTATION_TYPES.SET_MODAL_CONTEXT, {
          actorName: actor.name,
          communityName: this.community?.name,
          resource: actor,
          modalContextType: 'community-actor',
        })
        this.$store.commit(UI_MUTATION_TYPES.SHOW_MODAL, MODAL_IDS.DELETE_CONFIRMATION)
      },
      isRemoving (actor: any): boolean {
        return this.removingIds.includes(actor.id)
      },
      removeActor (actor: any) {
        if (this.isRemoving(actor)) {
          return
        }

        // Removing hits Elasticsearch and the portfolio sync, so it is not instant. The row stays
        // in place in a pending state until the server confirms, rather than jumping away later.
        this.removingIds = [...this.removingIds, actor.id]

        removeActorFromCommunity(this.communityId, actor.elastic_id)
          .then(() => {
            trackMatomoEvent(
              MATOMO_EVENT_CATEGORIES.COMMUNITY_MANAGEMENT,
              MATOMO_EVENT_ACTIONS.REMOVE_ACTOR_FROM_COMMUNITY,
              `{communityId: ${this.communityId},actorId: ${actor.elastic_id}}`,
            )

            this.actors = this.actors.filter((a: any) => a.id !== actor.id)

            if (this.total !== null) {
              this.total--
            }

            // Elasticsearch needs a moment to reflect the change, so the public actors tab is
            // refreshed through the same event the actor pages already listen to
            this.$bus.emit('actorUpdated')
          })
          .catch(() => {
            // Re-fetch so the list reflects the actual state after a failed removal
            this.fetch()
          })
          .finally(() => {
            this.removingIds = this.removingIds.filter((id: number) => id !== actor.id)
          })
      },
      setOffset (offset: number) {
        this.filters = { ...this.filters, offset }
      },
      fetch () {
        this.loading = true

        fetchCommunityActors(this.communityId, this.filters)
          .then((response: any) => {
            this.actors = response.data
            this.total = response.total
          })
          .finally(() => {
            this.loading = false
          })
      },
    },
    mounted () {
      this.fetch()
      this.$bus.on('communityActorDeleteConfirmation', (context: any) => this.removeActor(context.resource))
      this.$bus.on('communityActorsAdded', () => this.fetch())
    },
    beforeUnmount () {
      this.$bus.off('communityActorDeleteConfirmation')
      this.$bus.off('communityActorsAdded')
    },
    watch: {
      'filters.q' () {
        // A new search starts from the first page again
        this.filters.offset = 0
      },
      filters: {
        deep: true,
        handler: _throttle(function (this: any) {
          this.fetch()
        }, 300, { trailing: true }),
      },
    },
  })
</script>

<style lang="scss" scoped>
  @keyframes community-actors-spin {
    to {
      transform: rotate(360deg);
    }
  }

  .community-actors__row {
    transition: opacity 0.15s ease-in-out;
  }

  // Pending removal: the row reads as busy and stops accepting further input
  .community-actors__row--removing {
    opacity: 0.55;
    pointer-events: none;
  }

  // DsButton renders its own icon, so the spin has to be applied from the outside
  .community-actors__remove--busy :deep(.svg-icon) {
    animation: community-actors-spin 0.8s linear infinite;
  }

  // Overlapping stack, so a row with several contributors stays compact
  .community-actors__contributors {
    margin-right: 4px;
  }

  .community-actors__contributor {
    display: inline-flex;
    border-radius: 50%;
    border: 2px solid white;
    cursor: default;
    transition: transform 0.15s ease-in-out;

    &:not(:first-child) {
      margin-left: -8px;
    }
  }

  .community-actors__contributor--clickable {
    cursor: pointer;

    // Lift the hovered/focused avatar clear of the ones stacked on top of it
    &:hover,
    &:focus-visible {
      position: relative;
      z-index: 1;
      transform: scale(1.12);
    }

    &:focus-visible {
      outline: 2px solid var(--primary-community, #3b82f6);
      outline-offset: 1px;
    }
  }

  .community-actors__contributor-overflow {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 26px;
    height: 26px;
    margin-left: -8px;
    border-radius: 50%;
    border: 2px solid white;
    background: #e5e7eb;
    font-size: 0.65rem;
    font-weight: 600;
    color: #4b5563;
    cursor: default;
  }

  .community-actors__logo {
    width: 42px;
    height: 42px;
    border-radius: 8px;
    border: 1px solid #f3f4f6;
    background-color: #f9fafb;
    background-size: contain;
    background-position: center;
    background-repeat: no-repeat;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 0.75rem;
    font-weight: 600;
    color: #6b7280;
  }
</style>
